import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { BarChart3, Loader2 } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

interface RiskDimensions {
  data: number;
  operational: number;
  compliance: number;
  security: number;
  ethical: number;
  financial: number;
}

interface RiskAssessment {
  id: string;
  name: string;
  risk_level: RiskLevel;
  overall_score: number;
  dimensions: RiskDimensions;
  created_at: string;
}

interface RiskResponse {
  assessments: RiskAssessment[];
  stats: {
    total: number;
    average_score: number;
    high_critical: number;
    low_count: number;
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function riskBadge(level: RiskLevel) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (level) {
    case 'critical':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Critical</span>;
    case 'high':
      return <span className={`${base} bg-orange-500/15 text-orange-500`}>High</span>;
    case 'medium':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Medium</span>;
    case 'low':
      return <span className={`${base} bg-green-500/15 text-green-500`}>Low</span>;
  }
}

function barColor(score: number): string {
  if (score >= 75) return 'bg-red-500';
  if (score >= 50) return 'bg-orange-500';
  if (score >= 25) return 'bg-yellow-500';
  return 'bg-green-500';
}

const DIMENSION_LABELS: Record<keyof RiskDimensions, string> = {
  data: 'Data Risk',
  operational: 'Operational',
  compliance: 'Compliance',
  security: 'Security',
  ethical: 'Ethical',
  financial: 'Financial',
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RiskBar({ label, value }: { label: string; value: number }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xs font-medium text-foreground">{value}/100</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${barColor(value)} transition-all`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function AssessmentCard({ assessment }: { assessment: RiskAssessment }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-medium text-foreground truncate">{assessment.name}</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Score: <span className="font-medium text-foreground">{assessment.overall_score}</span>/100
          </p>
        </div>
        {riskBadge(assessment.risk_level)}
      </div>
      <div className="space-y-2">
        {(Object.keys(DIMENSION_LABELS) as (keyof RiskDimensions)[]).map((dim) => (
          <RiskBar key={dim} label={DIMENSION_LABELS[dim]} value={assessment.dimensions[dim]} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function RiskDashboardPage() {
  const riskQuery = useQuery<RiskResponse>({
    queryKey: ['governance', 'risk'],
    queryFn: () => api<RiskResponse>('governance/risk'),
  });

  const stats = riskQuery.data?.stats;
  const assessments = riskQuery.data?.assessments ?? [];

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <BarChart3 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Risk Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Assess and monitor risk scores across all AI systems and deployments.
          </p>
        </div>
      </div>

      {/* Stats */}
      {riskQuery.isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Total Assessments</p>
            <p className="text-2xl font-bold text-foreground">{stats?.total ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Average Risk Score</p>
            <p className="text-2xl font-bold text-foreground">{stats?.average_score ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">High / Critical</p>
            <p className="text-2xl font-bold text-red-500">{stats?.high_critical ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Low Risk</p>
            <p className="text-2xl font-bold text-green-500">{stats?.low_count ?? 0}</p>
          </div>
        </div>
      )}

      {/* Aggregate risk dimensions */}
      {riskQuery.isSuccess && assessments.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">Aggregate Risk Profile</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {(Object.keys(DIMENSION_LABELS) as (keyof RiskDimensions)[]).map((dim) => {
              const avg = Math.round(
                assessments.reduce((sum, a) => sum + a.dimensions[dim], 0) / assessments.length,
              );
              return <RiskBar key={dim} label={DIMENSION_LABELS[dim]} value={avg} />;
            })}
          </div>
        </div>
      )}

      {/* Assessment cards */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">Assessments</h2>

        {riskQuery.isLoading && (
          <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading assessments...</span>
          </div>
        )}

        {riskQuery.isSuccess && assessments.length === 0 && (
          <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
            No risk assessments yet. Run a governance scan to generate risk data.
          </div>
        )}

        {riskQuery.isSuccess && assessments.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assessments.map((assessment) => (
              <AssessmentCard key={assessment.id} assessment={assessment} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
